// ============================================================
//  Gate-Dokument als PDF — dieselben Zahlen wie im Modal, nichts dazu erfunden.
//  Reihenfolge: Stichtag → Systeme und Lizenzmodell → Bedarfsrechnung am Gate →
//  Berechtigungsbild → Risiken → Freigabeempfehlung → Trace-Zeitraum → Hinweis Beispieldaten.
// ============================================================
import type { Landscape } from "../domain/types";
import type { GateForecast } from "../domain/analysis";
import type { GateBefund } from "../domain/gateBefund";
import { GATE_STATUS_LABEL, type GateStatusEntry } from "../store/gateStatus";
import { landscapeLicensing } from "../data/licensing";
import { MODEL_CATEGORY, MODEL_LABEL } from "../domain/licensing";
import { contractedFue } from "../store/contract";
import { TRACE_META, traceWindowLabel } from "../data/traceMeta";
import { formatDateDe, formatFue, formatNum, formatPct, formatTage } from "../domain/rules";
import { pdfHeading, pdfKpis, pdfLines, pdfSave, pdfStart, pdfTable } from "./pdf";

export interface GatePdfInput {
  landscape: Landscape;
  /** Gate-Zahlen aus gateChain (auch für Engine-/Sondermetrik-Gates, dort +0). */
  gate?: GateForecast;
  /** Risiken, Konfidenz und Empfehlung — aus den Daten (domain/gateBefund.ts). */
  befund: GateBefund;
  /** Von Menschen gesetzter Status (store/gateStatus.ts). */
  status: GateStatusEntry;
  /** Vertragszahl (useContract().total) — Standard: Vertragsgrundlage aus dem Store. */
  contracted?: number;
  risk?: { persons: number; lifted: number; share: number };
  /** RFP-Datum, falls der Cutover im Plan verschoben wurde. */
  rfpCutover?: string;
  daysLeft: number | null;
}

/** Vertragsdifferenz als Text: Puffer (unter Vertrag) oder Lücke (über Vertrag). */
export function contractDeltaLabel(cumulative: number, contracted: number = contractedFue()): string {
  const d = contracted - cumulative;
  if (Math.round(d) === 0) return "Vertrag exakt ausgeschöpft";
  return d > 0 ? `Puffer ${formatFue(d)}` : `Lücke ${formatFue(-d)}`;
}

/** Dateiname: Gate_L6_GP7_2027-06-30.pdf — Sonderzeichen aus dem Landschaftsnamen raus. */
export function gatePdfFilename(l: Landscape): string {
  const name = l.name.replace(/\s*\/\s*/g, "-").replace(/\s*\+\s*/g, "-").replace(/[^A-Za-z0-9_-]+/g, "");
  return `Gate_${l.id}_${name}_${l.cutover ?? "ohne-Datum"}.pdf`;
}

/** „freigegeben" bzw. „freigegeben am 01.02.2027 durch Name". */
export function gateStatusLabel(s: GateStatusEntry): string {
  const base = GATE_STATUS_LABEL[s.status];
  const teile = [s.datum ? `am ${formatDateDe(s.datum)}` : "", s.von ? `durch ${s.von}` : ""].filter(Boolean);
  return teile.length ? `${base} ${teile.join(" ")}` : base;
}

/** Statuszeile im PDF: „Freigabe: freigegeben mit Auflage am … durch …" / „Freigabe: offen". */
export function gateStatusLine(s: GateStatusEntry): string {
  return `Freigabe: ${gateStatusLabel(s)}`;
}

export function exportGatePdf(input: GatePdfInput) {
  const { landscape: l, gate, befund, status, risk, rfpCutover, daysLeft } = input;
  const contracted = input.contracted ?? contractedFue();
  const delta = (cum: number) => contractDeltaLabel(cum, contracted);
  const p = pdfStart(`Gate-Dokument ${l.name}`, `${l.group} · ${l.unit} · Cutover ${formatDateDe(l.cutover)} · Beispieldaten`);

  pdfHeading(p, "Stichtag");
  pdfKpis(p, [
    ["Cutover", formatDateDe(l.cutover) + (rfpCutover && rfpCutover !== l.cutover ? ` (geändert, RFP-Plan: ${formatDateDe(rfpCutover)})` : "")],
    ["Vorlauf", daysLeft !== null && daysLeft >= 0 ? formatTage(daysLeft) : "Stichtag vergangen"],
    ["Welle", l.wave ? String(l.wave) : "—"],
    ["Mandanten", formatNum(l.clients)],
    ["Status Gate", gateStatusLabel(status)],
    ["Konfidenz Datenlage", befund.konfidenz.konten > 0 ? formatPct(befund.konfidenz.prozent, 0) : "—"],
  ]);

  pdfHeading(p, "Systeme und Lizenzmodell");
  const sys = landscapeLicensing(l.id);
  if (sys.length === 0) pdfLines(p, ["Keine Systeme zugeordnet."]);
  else pdfTable(p, ["System", "Kategorie", "Lizenzmodell", "Engine-Metrik", "Einstufung"], sys.map((s) => [
    s.sid, MODEL_CATEGORY[s.model], MODEL_LABEL[s.model],
    s.engine ? `${s.engine.name} — ${s.engine.einheit}${s.engine.wert === null ? " (Wert offen)" : ` · ${s.engine.wert}`}` : "—",
    s.belegt ? "belegt" : "Annahme",
  ]));

  pdfHeading(p, "Bedarfsrechnung am Gate");
  if (!gate) {
    pdfLines(p, ["Kein Cutover-Datum — kein Gate in der Kette."]);
  } else if (!gate.fueRelevant) {
    pdfKpis(p, [
      ["Bedarf aus diesem Cutover", "+0 FUE — kein FUE-Bedarf (eigene Lizenzmetrik)"],
      ["Kumulierter Bedarf vor dem Gate", formatFue(gate.cumulativeBefore)],
      ["Kumulierter Bedarf nach dem Gate", `${formatFue(gate.cumulative)} (unverändert)`],
      ["Vertrag", formatFue(contracted)],
      ["Differenz zum Vertrag", delta(gate.cumulative)],
    ]);
  } else {
    pdfKpis(p, [
      ["Bedarf dieses Systems", formatFue(gate.fueAdded)],
      ["davon vermeidbar", formatFue(gate.avoidable)],
      ["davon gefahrlos (nie genutzt)", formatFue(gate.avoidableSafe)],
      ["Zielwert nach Optimierung", formatFue(gate.target)],
      ["Personen (erstmals zählend)", formatNum(gate.persons)],
    ]);
    pdfTable(p, ["Kumulierter Bedarf", "FUE", `Vertrag ${formatFue(contracted)}`], [
      ["vor dem Gate", formatFue(gate.cumulativeBefore), delta(gate.cumulativeBefore)],
      [`+ Bedarf ${l.name}`, `+${formatFue(gate.fueAdded)}`, ""],
      ["= nach dem Gate", formatFue(gate.cumulative), delta(gate.cumulative)],
      ["− davon vermeidbar", `−${formatFue(gate.avoidable)}`, ""],
      ["= nach dem Gate mit Optimierung", formatFue(gate.cumulativeOptimized), delta(gate.cumulativeOptimized)],
    ], { fontSize: 9.5 });
  }

  if (risk) {
    pdfHeading(p, "Berechtigungsbild");
    pdfKpis(p, [
      ["Personen mit Bezug", formatNum(risk.persons)],
      ["über-berechtigt", formatNum(risk.lifted)],
      ["Anteil", formatPct(risk.share * 100, 0)],
      ...(gate && !gate.fueRelevant ? [["Lizenzwirkung", "keine — Sicherheitsthema, kein FUE"] as [string, string]] : []),
    ]);
  }

  pdfHeading(p, "Risiken aus den Daten");
  if (befund.risiken.length === 0) pdfLines(p, ["Keine Risiken nach Regelwerk (Countdown, Über-Berechtigung, Trace, Zuordnung, Vertrag)."]);
  else pdfTable(p, ["Schwere", "Risiko", "Wert", "Herleitung"], befund.risiken.map((r) => [r.schwere === "rot" ? "rot" : "gelb", r.label, r.wert, r.herleitung]), { fontSize: 8.5 });
  if (befund.hebel) pdfLines(p, [`Hebel (Chance): ${befund.hebel.label} ${befund.hebel.wert} — ${befund.hebel.herleitung}`]);
  if (befund.groessteMassnahme) {
    const m = befund.groessteMassnahme;
    pdfLines(p, [`Größte Maßnahme: ${m.roleName} (${m.sid}, ${m.authObject}) — −${formatFue(m.deltaFue)} bei ${formatNum(m.effective)} wirksamen Personen.`]);
  }
  pdfLines(p, [`Konfidenz Datenlage: ${befund.konfidenz.herleitung}`]);

  pdfHeading(p, "Freigabeempfehlung");
  pdfLines(p, [befund.empfehlung.text]);

  pdfHeading(p, "Status");
  pdfLines(p, [gateStatusLine(status)]);

  pdfHeading(p, "Nutzungsdaten (Trace)");
  pdfKpis(p, [
    ["Zeitraum", traceWindowLabel()],
    ["Quelle", TRACE_META.source],
  ]);

  pdfHeading(p, "Freigabe / Unterschriften");
  pdfLines(p, ["Lizenzverantwortlicher: ____________________    Datum: __________", "", "Projektleitung: ____________________    Datum: __________"]);

  pdfLines(p, ["Hinweis: Beispieldaten, kein Produktivsystem. Zahlen nach Regelwerk R1–R4 (Personen statt Konten, Klasse = Maximum, Hebel nur ohne zweiten Treiber, Zielzustand)."]);

  pdfSave(p, gatePdfFilename(l));
}
